import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemButton,
  Paper
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
  Play, 
  Clock, 
  Youtube,
  ExternalLink,
  ArrowLeft,
  BookOpen,
  Star
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Video {
  id: number;
  title: string;
  description: string;
  duration: string;
  thumbnail: string;
  youtubeId: string;
  timestamp: string;
}

interface CoursePageProps {
  title: string;
  subheading: string;
  description: string;
  punchLine: string;
  color: string;
  gradient: string;
  students: string;
  duration: string;
  lessons: number;
  image: string;
  videos: Video[];
  icon: React.ReactNode;
}

const CoursePage: React.FC<CoursePageProps> = ({
  title,
  subheading,
  description,
  punchLine,
  color,
  gradient,
  students,
  duration,
  lessons,
  image,
  videos,
  icon
}) => {
  const navigate = useNavigate();
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  const handleVideoClick = (video: Video) => {
    setSelectedVideo(video);
  };

  const handleSubscribe = () => {
    window.open('https://www.youtube.com/@RevivingIndia', '_blank');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#F8FAFC',
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' }
    }}>
      {/* Sidebar - theme: white card, blue accent */}
      <Paper
        elevation={0}
        sx={{
          width: { xs: '100%', md: '350px' },
          height: { xs: 'auto', md: '100vh' },
          bgcolor: '#FFFFFF',
          borderRight: '1px solid #E5E7EB',
          overflow: 'auto',
          position: { xs: 'relative', md: 'sticky' },
          top: 0,
          zIndex: 1
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton 
              onClick={() => navigate('/')}
              sx={{ mr: 1, color: '#64748B', '&:hover': { color: '#2563EB', bgcolor: '#EAF2FF' } }}
            >
              <ArrowLeft size={20} />
            </IconButton>
            {icon}
            <Typography variant="h6" sx={{ ml: 1, fontWeight: 600, color: '#1E293B' }}>
              {title}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.5 }}>
            {description}
          </Typography>
        </Box>

        {/* Progress */}
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <BookOpen size={16} color="#2563EB" />
            <Typography variant="body2" sx={{ ml: 1, fontWeight: 600, color: '#1E293B' }}>
              Course Progress
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#64748B' }}>
            {videos.length} tutorial videos available
          </Typography>
          <Box sx={{ 
            width: '100%', 
            height: 4, 
            bgcolor: '#E5E7EB', 
            borderRadius: 2, 
            mt: 1,
            overflow: 'hidden'
          }}>
            <Box sx={{
              width: '100%',
              height: '100%',
              bgcolor: '#2563EB',
              transition: 'width 0.3s ease'
            }} />
          </Box>
        </Box>

        {/* Videos List */}
        <List sx={{ p: 0 }}>
          {videos.map((video, index) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <ListItem sx={{ p: 0 }}>
                <ListItemButton
                  onClick={() => handleVideoClick(video)}
                  sx={{
                    p: 2,
                    borderBottom: '1px solid #E5E7EB',
                    '&:hover': {
                      bgcolor: '#EAF2FF',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 40, 
                      borderRadius: 1, 
                      overflow: 'hidden',
                      mr: 2,
                      position: 'relative',
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }}>
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          background: 'rgba(0, 0, 0, 0.7)',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Play size={12} color="white" />
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 600, 
                        mb: 0.5, 
                        fontSize: '0.9rem',
                        color: '#1E293B',
                        lineHeight: 1.4
                      }}>
                        {video.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Clock size={12} color="#64748B" />
                        <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.8rem' }}>
                          {video.duration}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>
            </motion.div>
          ))}
        </List>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {selectedVideo ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            {/* Video Header - theme */}
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid #E5E7EB',
              bgcolor: '#FFFFFF'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, fontSize: '1.5rem', color: '#1E293B', lineHeight: 1.3 }}>
                {selectedVideo.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip 
                  icon={<Clock size={16} />} 
                  label={selectedVideo.duration} 
                  size="small"
                  sx={{ bgcolor: '#EAF2FF', color: '#2563EB' }}
                />
                <Chip 
                  icon={<Star size={16} />} 
                  label="Tutorial" 
                  size="small"
                  sx={{ bgcolor: '#EAF2FF', color: '#2563EB' }}
                />
              </Box>
              <Typography variant="body1" sx={{ color: '#64748B', lineHeight: 1.6 }}>
                {selectedVideo.description}
              </Typography>
            </Box>

            {/* Video Player */}
            <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: '#FFFFFF',
                  p: 0,
                  borderRadius: 2,
                  border: '1px solid #E5E7EB',
                  mb: 3,
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: '250px', md: '400px', lg: '500px' },
                    background: '#000',
                  }}
                >
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}?start=${selectedVideo.timestamp}&autoplay=1&rel=0&modestbranding=1`}
                    title={selectedVideo.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </Box>
              </Paper>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<Youtube size={18} />}
                  onClick={handleSubscribe}
                  sx={{
                    bgcolor: '#ff0000',
                    color: 'white',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                    boxShadow: '0 2px 8px rgba(255, 0, 0, 0.3)',
                    '&:hover': {
                      bgcolor: '#cc0000',
                      boxShadow: '0 4px 16px rgba(255, 0, 0, 0.4)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  Watch on YouTube
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ExternalLink size={16} />}
                  onClick={handleSubscribe}
                  sx={{
                    borderColor: '#2563EB',
                    color: '#2563EB',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#1D4ED8',
                      bgcolor: '#EAF2FF',
                    }
                  }}
                >
                  Subscribe
                </Button>
              </Box>
            </Box>
          </motion.div>
        ) : (
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            p: 3,
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #EAF2FF 0%, #F8FAFC 50%, #E0E7FF 100%)'
          }}>

            {/* Main Content - themed to match site (primary blue, card style) */}
            <Box sx={{ 
              textAlign: 'center', 
              position: 'relative', 
              zIndex: 2,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(12px)',
              borderRadius: 3,
              p: 4,
              border: '1px solid #E5E7EB',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06)'
            }}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                {icon}
              </motion.div>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Typography variant="h5" sx={{ 
                  mt: 2, 
                  mb: 1, 
                  fontWeight: 600,
                  color: '#1E293B',
                  fontSize: '1.75rem'
                }}>
                  Welcome to {title}
                </Typography>
              </motion.div>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Typography variant="body1" sx={{ 
                  color: '#64748B', 
                  mb: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500
                }}>
                  Select a video from the sidebar to start learning
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#94a3b8', 
                  mb: 3,
                  fontSize: '0.9rem',
                  fontStyle: 'italic'
                }}>
                  More data and lessons coming soon.
                </Typography>
              </motion.div>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <Button
                  variant="contained"
                  startIcon={<Play size={16} />}
                  onClick={() => videos[0] && handleVideoClick(videos[0])}
                  sx={{
                    bgcolor: '#2563EB',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    textTransform: 'none',
                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
                    '&:hover': { 
                      bgcolor: '#1D4ED8',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Start with First Video
                </Button>
              </motion.div>
            </Box>
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default CoursePage;
